/**
 * THE LAST RESORT: A RENDER FAULT BECOMES A WRITTEN STATE INSTEAD OF A BLANK PAGE. 09 §5.8.
 *
 * There was no error boundary anywhere in `apps/web`. `main.tsx` mounted `StrictMode >
 * BrowserRouter > App`, so any exception thrown during render unmounted the whole tree and the
 * browser was left showing an empty `#root` — no title, no sentence, no reference code, nothing to
 * quote to support. Five screens did exactly that on every cold arrival, and the product's answer to
 * the Owner was a white page.
 *
 * ── IT ADDS NO PRODUCT BEHAVIOUR, AND THAT IS DELIBERATE ────────────────────────────────────────
 *
 * The Master is SILENT on render-fault containment: no clause says a screen must never render blank
 * and none requires a boundary (recorded as H-M9). So this file invents nothing. It renders the
 * generic error state the product ALREADY defines — `<ErrorState>` in `StateBlock.tsx`, whose exact
 * words are *"אירעה תקלה" / "לא הצלחנו להשלים את הפעולה."* — and not one new sentence is written
 * here. If the Master later defines this state, that clause governs and this file follows it.
 *
 * ── TWO THINGS IT MUST NOT DO ───────────────────────────────────────────────────────────────────
 *
 *   · IT MUST NOT FABRICATE A REFERENCE CODE. `requestUid` is `''`, because there is no request: a
 *     render fault happened in the browser and no server ever saw it. `<ErrorState>` already renders
 *     the empty case as `'לא זמין'`, which is the truthful answer. Inventing a code here would hand
 *     the user a string support cannot find, which is worse than admitting there is none.
 *   · IT MUST NOT PRINT THE EXCEPTION. 09 §5.8 and §9.1 V3 — never an English exception, never a
 *     stack trace. The fault goes to `console.error`, where a developer and a browser session
 *     recording can see it; the screen says only that the action did not complete.
 *
 * It does not swallow the fault silently either: the state it renders IS the report, and the console
 * carries the detail. And it deliberately has no "try again" reset — every navigation in this app is
 * a full page load (F.2), so the reload the user already has is the honest recovery, and a reset
 * button that re-rendered the same broken tree would loop.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from './StateBlock';

export interface RenderFaultBoundaryProps {
  readonly children: ReactNode;
}

interface RenderFaultBoundaryState {
  readonly faulted: boolean;
}

export class RenderFaultBoundary extends Component<
  RenderFaultBoundaryProps,
  RenderFaultBoundaryState
> {
  public override state: RenderFaultBoundaryState = { faulted: false };

  /** The error itself is NOT kept in state — nothing may render it. Only the fact that one occurred. */
  public static getDerivedStateFromError(): RenderFaultBoundaryState {
    return { faulted: true };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The one place the detail is allowed to go. Not the screen.
    console.error('render fault contained by RenderFaultBoundary', error, info.componentStack);
  }

  public override render(): ReactNode {
    if (this.state.faulted) {
      /*
       * WHAT HAPPENED AND WHAT TO DO, for the one failure this component is about. **2026-10-25.**
       *
       * A render fault is the case where the product knows MORE than usual, not less: the screen
       * failed to draw, in this browser, and no request was involved — which is also why there is
       * no support reference. Reloading genuinely is the next step, and it was not being offered.
       *
       * Still no exception text and no stack frame (09 §9.1 V3). What is added is a sentence about
       * the SITUATION, which gives away nothing about the fault.
       */
      return (
        <ErrorState
          requestUid=""
          whatHappenedHe="המסך לא הצליח להיטען. התקלה אירעה בדפדפן, ולא נשלחה בקשה לשרת — ולכן שום נתון לא השתנה."
          whatToDoHe="יש לרענן את העמוד. אם זה חוזר על עצמו, יש לפנות לתמיכה ולציין באיזה מסך זה קרה."
        />
      );
    }
    return this.props.children;
  }
}
